import AWS from "aws-sdk";
import axios from "axios";
import config from "../config";
import { getAccessToken } from "../utils";
import {
  updateS3FileUrl,
  updateS3UrlFormdata,
} from "../indexDB/utils/datapoint";
import { db } from "../indexDB/model";
import { handleApiError } from "../components/errorBoundary/errorUtils";

// Retrieve the current domain and its configuration
const currentDomain = process.env.REACT_APP_DOMAIN;
const domainConfig = config[currentDomain];
const endpoint = domainConfig.apiEndpoint;
const bucketName = domainConfig.bucketName;

// Configure AWS SDK
AWS.config.update({
  region: "ap-south-1",
});

/**
 * Retrieve the UUID of the current datapoint.
 * @param {number} id - Datapoint ID.
 * @returns {Promise<string>} - Promise that resolves to the UUID.
 */
async function getCurrentDatapointID(id) {
  const datapoint = await db.datapoints.where("id").equals(id).first();
  return datapoint.uuid;
}

/**
 * Check if a timestamp is expired, with an optional buffer.
 * @param {number} expirationTimestamp - Expiration timestamp to check.
 * @param {number} [bufferMinutes=10] - Optional buffer in minutes.
 * @returns {boolean} - Whether the timestamp is expired.
 */
function isExpiredWithBuffer(expirationTimestamp, bufferMinutes = 10) {
  const expirationDate = new Date(expirationTimestamp);
  const currentTime = new Date();
  const currentTimeWithBuffer = new Date(
    currentTime.getTime() + bufferMinutes * 60 * 1000
  );
  return currentTimeWithBuffer >= expirationDate;
}

/**
 * Obtain temporary AWS STS credentials.
 * @returns {Promise<object>} - Promise that resolves to STS credentials.
 */
async function getStsToken() {
  try {
    const currentTime = sessionStorage.getItem("Expiration");
    const AccessKeyId = sessionStorage.getItem("AccessKeyId");
    const SecretAccessKey = sessionStorage.getItem("SecretAccessKey");
    const SessionToken = sessionStorage.getItem("SessionToken");

    if (
      !currentTime ||
      !AccessKeyId ||
      !SecretAccessKey ||
      !SessionToken ||
      isExpiredWithBuffer(currentTime)
    ) {
      const accessToken = getAccessToken();
      const response = await axios.post(
        `${endpoint}project/generate-temp-credentials`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // Store the obtained STS credentials in session storage
      sessionStorage.setItem("AccessKeyId", response.data.AccessKeyId);
      sessionStorage.setItem("SecretAccessKey", response.data.SecretAccessKey);
      sessionStorage.setItem("SessionToken", response.data.SessionToken);
      sessionStorage.setItem("Expiration", response.data.Expiration);

      return response.data;
    } else {
      // Return STS credentials from session storage if not expired
      const AccessKeyId = sessionStorage.getItem("AccessKeyId");
      const SecretAccessKey = sessionStorage.getItem("SecretAccessKey");
      const SessionToken = sessionStorage.getItem("SessionToken");

      return {
        AccessKeyId,
        SecretAccessKey,
        SessionToken,
      };
    }
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * Upload files associated with a datapoint to Amazon S3.
 * @param {number} id - Datapoint ID.
 * @param {string} uuid - Datapoint UUID.
 * @returns {Promise<boolean>} - Promise that resolves to `true` if successful.
 */
async function uploadFilesToS3(id, uuid) {
  try {
    // Retrieve files associated with the datapoint
    const files = await db.fileData.where("datapointID").equals(id).toArray();

    // Iterate through files and upload to S3 if not already uploaded
    for (const file of files) {
      if (!file.url) {
        await uploadSingleFileToS3(file.data, file.id, uuid, id);
      }
    }

    return true;
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * Retrieve project, dataset, and user information for a datapoint.
 * @param {number} id - Datapoint ID.
 * @returns {Promise<object>} - Promise that resolves to keys containing user, project, and dataset information.
 */
async function getKeys(id) {
  try {
    // Retrieve datapoint, dataset, and project information
    const datapoint = await db.datapoints.where("id").equals(id).first();
    const dataset = await db.datasets
      .where("uuid")
      .equals(datapoint.datasetID)
      .first();
    const project = await db.projects
      .where("uuid")
      .equals(dataset.projectID)
      .first();
    const username = localStorage.getItem("username");

    return {
      user: username,
      project: project.name,
      dataset: dataset.name,
    };
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * Upload a single file to Amazon S3 using multipart upload.
 * @param {File} file - File to upload.
 * @param {number} id - FileData ID.
 * @param {string} uuid - Datapoint UUID.
 * @param {number} datapointID - Datapoint ID.
 * @returns {Promise<void>} - Promise that resolves upon successful upload.
 */
async function uploadSingleFileToS3(file, id, uuid, datapointID) {
  try {
    // Obtain temporary STS credentials
    const stsToken = await getStsToken();

    // Create an S3 instance with the obtained credentials
    const s3 = new AWS.S3({
      accessKeyId: stsToken.AccessKeyId,
      secretAccessKey: stsToken.SecretAccessKey,
      sessionToken: stsToken.SessionToken,
      region: "ap-south-1",
    });

    // Define S3 bucket name and key for the file

    const keys = await getKeys(datapointID);
    const key = `media/${keys.project}/${keys.dataset}/${uuid}/${file.name}`;

    // Step 1: Initiate the multipart upload
    const initiateResponse = await s3
      .createMultipartUpload({
        Bucket: bucketName,
        Key: key,
      })
      .promise();

    const uploadId = initiateResponse.UploadId;
    const partSize = 5 * 1024 * 1024; // 5 MB part size (adjust as needed)

    // Step 2: Upload parts
    const parts = [];
    let offset = 0;

    while (offset < file.size) {
      const end = Math.min(offset + partSize, file.size);
      const part = file.slice(offset, end);

      const partParams = {
        Bucket: bucketName,
        Key: key,
        PartNumber: parts.length + 1,
        UploadId: uploadId,
        Body: part,
      };

      const uploadPartResponse = await s3.uploadPart(partParams).promise();
      parts.push({
        ETag: uploadPartResponse.ETag,
        PartNumber: parts.length + 1,
      });

      offset += partSize;
    }

    // Step 3: Complete the multipart upload
    const completeParams = {
      Bucket: bucketName,
      Key: key,
      UploadId: uploadId,
      MultipartUpload: {
        Parts: parts,
      },
    };

    const completeResponse = await s3
      .completeMultipartUpload(completeParams)
      .promise();

    // Update URLs or perform any other necessary actions
    await updateS3FileUrl(id, completeResponse.Location);
    await updateS3UrlFormdata(id, completeResponse.Location);

    console.log("File uploaded successfully:", completeResponse.Location);
  } catch (error) {
    return handleApiError(error);
  }
}

// Export functions for external use
export { uploadFilesToS3 };
